import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') || categories[0].id;

  const filteredArticles = articles.filter(
    (article) => article.categoryId === activeCategory
  );

  const handleCategoryChange = (categoryId: string) => {
    setSearchParams({ cat: categoryId });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const currentCategory = categories.find((c) => c.id === activeCategory);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `mrds ${currentCategory?.name || '分类'} — 文章列表`,
    description: currentCategory?.description || 'mrds 文章分类页面',
    url: `https://mrds.io/categories?cat=${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'mrds 官方平台',
      url: 'https://mrds.io',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '分类' },
  ];

  return (
    <>
      <SEOHead
        title={`${currentCategory?.name || '分类'} — 文章列表`}
        description={currentCategory?.description || 'mrds 文章分类页面，浏览各类 mrds 相关文章。'}
        keywords={`mrds,${currentCategory?.name},mrds 教程,mrds 功能`}
        canonicalUrl={`https://mrds.io/categories?cat=${activeCategory}`}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {currentCategory?.name || '文章分类'}
            </h1>
            <p className="text-foreground-500 text-sm">
              {currentCategory?.description}
            </p>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-8">
          <div className="flex gap-2 flex-wrap mb-10">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${category.icon} text-sm`}></i>
                {category.name}
              </button>
            ))}
          </div>

          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full bg-background-50/85 backdrop-blur-sm text-foreground-700 text-xs font-medium">
                        {currentCategory?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed mb-4 line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}